import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import {
  formatJsonStringForEditor,
  JsonInputField,
  parseJsonString,
  stringifyJsonForApi,
} from '@/components/form-components/JsonInput'
import { Switch } from '@/components/shadcn/switch'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentWorkspace } from '../useCurrentWorkspace'
import { CronExpressionField } from './cronExpression/CronExpressionField'
import { DEFAULT_CRON_BUILDER_STATE, buildCronExpression } from './cronExpression/cronExpressionUtils'

type ExistingSchedule = RestSchemas['Messages.Scheduler.ExistingSchedule']
type NewSchedule = RestSchemas['Messages.Scheduler.NewSchedule']

const formSchema = z
  .object({
    description: z.string().max(256, 'Description cannot exceed 256 characters').optional().default(''),
    cronExpression: z
      .string()
      .min(1, 'Cron expression is required')
      .max(120, 'Cron expression cannot exceed 120 characters'),
    contentId: z.string().min(1, 'Content id is required').max(256, 'Content id cannot exceed 256 characters'),
    filters: z.string().optional().default(''),
    payload: z.string().optional().default(''),
    enabled: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    for (const field of ['filters', 'payload'] as const) {
      const value = data[field]?.trim() ?? ''
      if (!value) continue

      const result = parseJsonString(value)
      if (!result.valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: result.message,
        })
      }
    }
  })

type TEventSchedulerForm = z.infer<typeof formSchema>

type EventSchedulerFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedSchedule?: ExistingSchedule
}

const emptyToNull = (value: string) => {
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

const jsonFieldToApi = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return null
  return stringifyJsonForApi(trimmed)
}

export const EventSchedulerForm = (props: EventSchedulerFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const { workspaceId } = useCurrentWorkspace()
  const edit = !!props.selectedSchedule
  const { getSchedulerApiGetSchedules, putSchedulerApiAddSchedule, postSchedulerApiUpdateSchedule } = useRestClient()

  const invalidateSchedules = useCallback(() => {
    getSchedulerApiGetSchedules.invalidateQueries({
      parameters: {
        path: { workspaceId },
      },
    })
  }, [getSchedulerApiGetSchedules, workspaceId])

  const { mutate: createSchedule, isPending: isCreating } = putSchedulerApiAddSchedule.useMutation(undefined, {
    onSuccess: () => {
      invalidateSchedules()
      if (props.sheetTrigger) setSheetOpen(false)
      toast.success('Schedule created')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Failed to create schedule')
    },
  })

  const { mutate: updateSchedule, isPending: isUpdating } = postSchedulerApiUpdateSchedule.useMutation(undefined, {
    onSuccess: () => {
      invalidateSchedules()
      if (props.sheetTrigger) setSheetOpen(false)
      toast.success('Schedule updated')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Failed to update schedule')
    },
  })

  const submitHandler = useCallback(
    (data: TEventSchedulerForm) => {
      if (edit && props.selectedSchedule) {
        const body: ExistingSchedule = {
          ...props.selectedSchedule,
          workspaceId,
          description: emptyToNull(data.description),
          cronExpression: data.cronExpression.trim(),
          contentId: data.contentId.trim(),
          filters: jsonFieldToApi(data.filters),
          payload: jsonFieldToApi(data.payload),
          enabled: data.enabled,
          id: props.selectedSchedule.id,
        }

        updateSchedule({
          body,
          path: {
            workspaceId,
            scheduleId: props.selectedSchedule.id,
          },
        })
        return
      }

      const body: NewSchedule = {
        workspaceId,
        description: emptyToNull(data.description),
        cronExpression: data.cronExpression.trim(),
        contentId: data.contentId.trim(),
        filters: jsonFieldToApi(data.filters),
        payload: jsonFieldToApi(data.payload),
        enabled: data.enabled,
      }

      createSchedule({
        body,
        path: { workspaceId },
      })
    },
    [createSchedule, edit, props.selectedSchedule, updateSchedule, workspaceId]
  )

  if (isCreating || isUpdating) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} edit={edit} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} edit={edit} submitHandler={submitHandler} />
  )
}

function FormComponent({
  selectedSchedule,
  submitHandler,
  edit,
}: EventSchedulerFormProps & {
  edit: boolean
  submitHandler: (data: TEventSchedulerForm) => void
}) {
  const defaultValues = useMemo(
    () => ({
      description: selectedSchedule?.description ?? '',
      cronExpression: selectedSchedule?.cronExpression ?? buildCronExpression(DEFAULT_CRON_BUILDER_STATE),
      contentId: selectedSchedule?.contentId ?? '',
      filters: formatJsonStringForEditor(selectedSchedule?.filters),
      payload: formatJsonStringForEditor(selectedSchedule?.payload),
      enabled: selectedSchedule?.enabled ?? true,
    }),
    [
      selectedSchedule?.contentId,
      selectedSchedule?.cronExpression,
      selectedSchedule?.description,
      selectedSchedule?.enabled,
      selectedSchedule?.filters,
      selectedSchedule?.payload,
    ]
  )

  const form = useForm<TEventSchedulerForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onTouched',
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submitHandler)}>
        <div className="flex h-full flex-col">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cronExpression"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Schedule</FormLabel>
                  <FormControl>
                    <CronExpressionField
                      portalPopover={false}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contentId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Event handler id</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <JsonInputField control={form.control} name="filters" label="Filters (JSON)" height="180px" />
            <JsonInputField control={form.control} name="payload" label="Payload (JSON)" height="180px" />
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel>Enabled</FormLabel>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="form-action-container">
            <Button type="submit">{edit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
